import React from 'react';

import { alpha } from '@material-ui/core/styles';
import moment from 'moment';
import PropTypes from 'prop-types';

// Components
import { Mui, TextField } from 'components';

const EXAMPLE_DUE_DAY = 10;

const MAX_MINIMUM_DAYS = 180;

const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';

const SELECTION_OPTIONS = [
  {
    name: 'dueDaySelectionEnabled',
    icon: 'event',
    title: 'Escolha do dia de vencimento',
    description: 'O responsável escolhe o dia de vencimento durante a compra da matrícula na loja.',
  },
  {
    name: 'recurrenceSelectionEnabled',
    icon: 'format_list_numbered',
    title: 'Escolha da quantidade de parcelas',
    description:
      'O responsável escolhe a quantidade de parcelas durante a compra da matrícula na loja.',
  },
];

const useStyles = Mui.makeStyles(theme => ({
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    height: '100%',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    transition: theme.transitions.create(['border-color', 'background-color']),
  },
  optionActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  optionIcon: {
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  optionIconActive: {
    color: theme.palette.primary.main,
  },
  example: {
    height: '100%',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  timeline: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  step: {
    flex: 1,
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  stepHighlight: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  arrow: {
    color: theme.palette.text.disabled,
    [theme.breakpoints.down('xs')]: {
      alignSelf: 'center',
      transform: 'rotate(90deg)',
    },
  },
}));

function getFirstInstallmentExample(minimumDays) {
  const days = Math.min(Math.max(Number.parseInt(minimumDays, 10) || 0, 0), MAX_MINIMUM_DAYS);
  const startDate = moment().startOf('day');
  const minimumDate = startDate.clone().add(days, 'days');
  const firstDueDate = minimumDate.clone().date(EXAMPLE_DUE_DAY);
  if (firstDueDate.isBefore(minimumDate)) firstDueDate.add(1, 'month').date(EXAMPLE_DUE_DAY);
  return {
    days,
    startDate: startDate.format(DISPLAY_DATE_FORMAT),
    minimumDate: minimumDate.format(DISPLAY_DATE_FORMAT),
    firstDueDate: firstDueDate.format(DISPLAY_DATE_FORMAT),
  };
}

function SelectionOption({ checked, disabled, option, onChange }) {
  // Hooks
  const classes = useStyles();

  return (
    <Mui.Box
      component="label"
      className={`${classes.option} ${checked ? classes.optionActive : ''}`}>
      <Mui.Icon className={`${classes.optionIcon} ${checked ? classes.optionIconActive : ''}`}>
        {option.icon}
      </Mui.Icon>
      <Mui.Box flex={1}>
        <Mui.Typography variant="subtitle1">{option.title}</Mui.Typography>
        <Mui.Typography variant="body2" color="textSecondary">
          {option.description}
        </Mui.Typography>
      </Mui.Box>
      <Mui.Switch
        color="primary"
        checked={checked}
        disabled={disabled}
        onChange={event => onChange(option.name, event.target.checked)}
      />
    </Mui.Box>
  );
}

SelectionOption.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  option: PropTypes.object,
  onChange: PropTypes.func,
};

function TimelineStep({ label, value, highlight }) {
  // Hooks
  const classes = useStyles();

  return (
    <Mui.Box className={`${classes.step} ${highlight ? classes.stepHighlight : ''}`}>
      <Mui.Typography variant="caption" color="textSecondary" display="block">
        {label}
      </Mui.Typography>
      <Mui.Typography variant="subtitle2" color={highlight ? 'primary' : 'textPrimary'}>
        {value}
      </Mui.Typography>
    </Mui.Box>
  );
}

TimelineStep.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  highlight: PropTypes.bool,
};

function EnrollmentPaymentSettings({ formik, formMessages }) {
  // Hooks
  const classes = useStyles();

  // Constants
  const example = getFirstInstallmentExample(formik.values.minimumFirstInstallmentDays);

  // Actions
  const handleToggle = (name, checked) => {
    formik.setFieldTouched(name, true);
    formik.setFieldValue(name, checked);
  };

  return (
    <Mui.Card variant="outlined">
      <Mui.CardHeader
        title={<Mui.Typography variant="h5">Condições de pagamento da matrícula</Mui.Typography>}
        subheader="Personalize as opções disponíveis para o responsável durante a compra da matrícula na loja."
      />
      <Mui.CardContent>
        <Mui.Grid container spacing={2}>
          {SELECTION_OPTIONS.filter(
            option =>
              option.name !== 'recurrenceSelectionEnabled' ||
              formik.values.paymentPlan === 'monthly',
          ).map(option => (
            <Mui.Grid key={option.name} item xs={12} md={6}>
              <SelectionOption
                option={option}
                checked={!!formik.values[option.name]}
                disabled={formik.isSubmitting}
                onChange={handleToggle}
              />
            </Mui.Grid>
          ))}
        </Mui.Grid>
        <Mui.Box my={3}>
          <Mui.Divider />
        </Mui.Box>
        <Mui.Box mb={2}>
          <Mui.Typography variant="subtitle1">
            Prazo mínimo para o primeiro vencimento
          </Mui.Typography>
          <Mui.Typography variant="body2" color="textSecondary">
            Quantidade mínima de dias entre o início da negociação e o primeiro vencimento. O prazo
            não define que a parcela vencerá exatamente após esses dias: o sistema respeita o dia de
            vencimento configurado ou escolhido e usa o primeiro vencimento disponível que cumpra o
            intervalo mínimo.
          </Mui.Typography>
        </Mui.Box>
        <Mui.Grid container spacing={2}>
          <Mui.Grid item xs={12} md={4}>
            <TextField
              required
              name="minimumFirstInstallmentDays"
              type="number"
              formik={formik}
              messages={formMessages}
              disabled={formik.isSubmitting}
              margin="none"
              inputProps={{ min: 0, max: MAX_MINIMUM_DAYS, step: 1 }}
              envelope="custom"
              append={{ content: 'dias', position: 'end' }}
            />
          </Mui.Grid>
          <Mui.Grid item xs={12} md={8}>
            <Mui.Box className={classes.example}>
              <Mui.Box display="flex" alignItems="center" gridGap={8}>
                <Mui.Icon fontSize="small" color="action">
                  lightbulb
                </Mui.Icon>
                <Mui.Typography variant="body2">
                  Exemplo: negociação iniciada hoje e responsável escolhendo o vencimento no{' '}
                  <strong>dia {EXAMPLE_DUE_DAY}</strong>
                </Mui.Typography>
              </Mui.Box>
              <Mui.Box className={classes.timeline}>
                <TimelineStep label="Início da negociação" value={example.startDate} />
                <Mui.Icon className={classes.arrow}>arrow_forward</Mui.Icon>
                <TimelineStep
                  label={`Data mínima (+${example.days} dias)`}
                  value={example.minimumDate}
                />
                <Mui.Icon className={classes.arrow}>arrow_forward</Mui.Icon>
                <TimelineStep label="Primeiro vencimento" value={example.firstDueDate} highlight />
              </Mui.Box>
            </Mui.Box>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.CardContent>
    </Mui.Card>
  );
}

EnrollmentPaymentSettings.propTypes = {
  formik: PropTypes.object,
  formMessages: PropTypes.object,
};

export default EnrollmentPaymentSettings;
